import * as Cesium from "cesium";

/** 与原先片元着色器一致：[255,255,255] => 偏青绿深色科技风 */
const FILTER_RGB = [14, 148, 125];

/**
 * 反色 + RGB 滤镜（等效原 globe 着色器注入，但不改私有 API）
 * Cesium 1.143 改 _surfaceShaderSet 会在缩放换瓦片时触发 VertexArray destroyed
 */
function applyInvertFilter(image, filterRGB) {
  const width = image.width;
  const height = image.height;
  if (!width || !height) return image;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) return image;

  ctx.filter = "invert(1)";
  ctx.drawImage(image, 0, 0, width, height);
  ctx.filter = "none";
  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = `rgb(${filterRGB[0]}, ${filterRGB[1]}, ${filterRGB[2]})`;
  ctx.fillRect(0, 0, width, height);
  ctx.globalCompositeOperation = "source-over";
  return canvas;
}

function wrapImageryProvider(provider, filterRGB) {
  if (!provider || provider._xcDarkFilter) return provider;

  const cache = new Map();
  const MAX_CACHE = 256;

  const wrapped = {
    _xcDarkFilter: true,
    get tileWidth() {
      return provider.tileWidth;
    },
    get tileHeight() {
      return provider.tileHeight;
    },
    get maximumLevel() {
      return provider.maximumLevel;
    },
    get minimumLevel() {
      return provider.minimumLevel;
    },
    get tilingScheme() {
      return provider.tilingScheme;
    },
    get rectangle() {
      return provider.rectangle;
    },
    get tileDiscardPolicy() {
      return provider.tileDiscardPolicy;
    },
    get errorEvent() {
      return provider.errorEvent;
    },
    get credit() {
      return provider.credit;
    },
    // 必须 false：带 alpha 的影像会进半透明通道，缩放丢瓦片时易 VertexArray destroyed
    get hasAlphaChannel() {
      return false;
    },
    get proxy() {
      return provider.proxy;
    },
    requestImage(x, y, level, request) {
      const key = `${level}/${x}/${y}`;
      if (cache.has(key)) return Promise.resolve(cache.get(key));

      return Promise.resolve(provider.requestImage(x, y, level, request)).then(
        (image) => {
          if (!image) return image;
          const filtered = applyInvertFilter(image, filterRGB);
          if (cache.size >= MAX_CACHE) {
            cache.delete(cache.keys().next().value);
          }
          cache.set(key, filtered);
          return filtered;
        }
      );
    },
    pickFeatures(x, y, level, longitude, latitude) {
      return provider.pickFeatures?.(x, y, level, longitude, latitude);
    },
  };

  return wrapped;
}

/**
 * 深色科技风底图（反色 + RGB 滤镜，不改 globe 私有着色器）
 */
export default function modifyMap(viewer) {
  if (!viewer || viewer.isDestroyed?.()) return;

  const layers = viewer.imageryLayers;
  layers.removeAll();

  Cesium.createWorldImageryAsync()
    .then((provider) => {
      if (viewer.isDestroyed?.()) return;
      const filtered = wrapImageryProvider(provider, FILTER_RGB);
      layers.addImageryProvider(filtered);
    })
    .catch((err) => {
      console.warn("[modifyMap] 深色底图加载失败，回退默认影像", err);
      Cesium.createWorldImageryAsync()
        .then((provider) => {
          if (!viewer.isDestroyed?.()) layers.addImageryProvider(provider);
        })
        .catch(() => {});
    });
}
